import { useQuery } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import { OpRow, CATS } from "@/components/dashboard/OpRow";
import { api } from "@/lib/api";
import { useFormat } from "@/lib/format";

const COLORS = ["#87B8A5", "#D8BD78", "#9DB4D8", "#C9A6D8", "#E3A79A", "#A8C8A0", "#B8B0A0", "#7FA7B8", "#D9A27E"];

export const MonthDetail = ({ row, onBack }) => {
  const { money } = useFormat();
  const { data } = useQuery({ queryKey: ["planning", "personal", "me", "operations", row.month], queryFn: () => api.get(`/planning/personal/me/operations?month=${row.month}`).then((r) => r.data) });
  const debits = (data?.items || []).filter((o) => o.amount > 0);
  const total = debits.reduce((s, o) => s + o.amount, 0);
  const cats = Object.values(debits.reduce((acc, o) => {
    const c = acc[o.category] || (acc[o.category] = { key: o.category, amount: 0, count: 0 });
    c.amount += o.amount; c.count += 1; return acc;
  }, {})).sort((a, b) => b.amount - a.amount);
  const top = [...debits].sort((a, b) => b.amount - a.amount).slice(0, 8);
  const title = new Date(`${row.month}-01T12:00:00`).toLocaleDateString("fr-FR", { month: "long", year: "numeric" });
  return (
    <div className="space-y-4" data-testid={`month-detail-${row.month}`}>
      <button onClick={onBack} className="flex items-center gap-1 text-xs font-semibold text-primary" data-testid="month-detail-back"><ArrowLeft className="h-3.5 w-3.5" />Toute l'année</button>
      <div>
        <h3 className="font-heading text-xl font-bold capitalize">{title}</h3>
        <p className="text-sm text-muted-foreground"><b className="num text-foreground" data-testid="month-detail-total">{money(row.amount)}</b> dépensés{row.previous != null && ` · ${money(row.previous)} l'an dernier`}{row.delta_pct != null && <span className={row.delta_pct > 0 ? "font-semibold text-destructive" : "font-semibold text-success"}> ({row.delta_pct > 0 ? "+" : ""}{row.delta_pct} %)</span>}</p>
      </div>
      {!data ? <div className="h-40 animate-pulse rounded-2xl bg-muted" /> : !debits.length ? (
        <p className="rounded-xl bg-secondary/60 p-4 text-sm text-muted-foreground" data-testid="month-detail-empty">Aucune opération enregistrée pour ce mois{row.amount ? " : le montant affiché provient de l'historique fictif du compte démo." : "."}</p>
      ) : (
        <>
          <section data-testid="month-detail-categories">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Par catégorie</div>
            <div className="mb-3 flex h-2.5 overflow-hidden rounded-full bg-secondary">{cats.map((c, i) => <div key={c.key} style={{ width: `${(c.amount / total) * 100}%`, background: COLORS[i % COLORS.length] }} />)}</div>
            <ul className="space-y-1.5">{cats.map((c, i) => (
              <li key={c.key} className="flex items-center gap-2 text-sm" data-testid={`month-cat-${c.key}`}>
                <i className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: COLORS[i % COLORS.length] }} />
                <span className="min-w-0 flex-1 truncate">{CATS[c.key] || c.key} <span className="text-[11px] text-muted-foreground">· {c.count} op.</span></span>
                <span className="num shrink-0 font-semibold">{money(c.amount)}</span>
                <span className="num w-10 shrink-0 text-right text-[11px] text-muted-foreground">{Math.round((c.amount / total) * 100)} %</span>
              </li>))}</ul>
          </section>
          <section data-testid="month-detail-top">
            <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Plus grosses opérations <span className="font-normal normal-case">· touchez pour modifier ou pointer</span></div>
            <ol className="divide-y divide-border">{top.map((o, i) => <OpRow key={o.id} o={o} i={i} />)}</ol>
          </section>
        </>
      )}
    </div>
  );
};
